<script setup lang="ts">
import { computed, ref } from "vue"
import { useRoute } from "vue-router/composables"
import { isNullish } from "@tinyurldev/tu-ui-vue"

import OrgSettingsLayout from "@Modules/teams/Views/OrgSettingsLayout.vue"
import OrgProfileSettings from "@Modules/teams/Views/OrgProfileSettings.vue"
import { XTeamsStore } from "@Modules/teams/Store/XTeamsStore.ts"
import type { Organization } from "@Modules/teams/Data/Models/OrganizationModel.ts"
import { logError } from "@Resources/js/helpers/logger"

//
// Setup
const route = useRoute()
const teams = XTeamsStore()
const isLoading = ref(false)

//
// Computed

/** The slug of the current organization and team */
const orgSlug = computed<string>(() => route?.params?.org || "")
const teamSlug = computed<string | null>(() => route.params?.team || null)

/** The currently loaded organization */
const currentOrganization = computed<Organization | null>(() =>
  teams.getOrganizationBySlug(orgSlug.value),
)

/** The currently chosen workspace. Personal team is 'personal'. Null if no workspace chosen */
const currentWorkspace = computed(() => {
  if (isNullish(teamSlug.value)) {
    return null
  }

  return teams.getWorkspaceBySlugAndTeam(orgSlug.value, teamSlug.value) ?? null
})

/** A list of validation errors when submitting the form */
const formErrors = computed(() => teams.errors || {});
const can = computed(() => currentOrganization.value?.can([ "update_organization" ]) || false);

//
// Methods
function onUpdate(name?: string | null, picture?: File | null): void {
  if (!currentOrganization.value || isLoading.value) {
    return;
  }

  isLoading.value = true;

  teams.updateOrganization(currentOrganization.value, {
    name: name ?? currentOrganization.value.name,
    description: currentOrganization.value.description ?? "",
    photo: picture,
  })
    .catch(() => { logError("[onUpdate]: Organization profile settings update failed!") })
    .finally(() => { isLoading.value = false; });
}
</script>

<template>
  <OrgSettingsLayout
    v-if="currentOrganization"
    :organization="currentOrganization"
    :workspace="currentWorkspace"
    :can="can"
  >
    <OrgProfileSettings
      :organization="currentOrganization"
      :errors="formErrors"
      :loading="isLoading"
      @update-icon="(picture) => onUpdate(null, picture)"
      @update-name="(name) => onUpdate(name)"
    />
  </OrgSettingsLayout>
</template>
